import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import WebApp from "@twa-dev/sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useNavigate,
  useNavigationType,
  useParams,
  useSearchParams,
} from "react-router-dom";

import {
  addColdSearchResultToTalentPool,
  deleteCandidateNote,
  dismissColdSearchResult,
  fetchApplicantTags,
  fetchCandidate,
  fetchCandidateApplications,
  fetchCandidateColdResult,
  fetchCandidateCv,
  fetchCandidateMatch,
  fetchCandidateNotes,
  fetchPipelineStages,
  postCandidateNote,
  sendColdSearchResultToPeopleForce,
} from "@/api/queries";
import ColdResultActions from "@/components/ColdResultActions";
import ScoreBadge from "@/components/ScoreBadge";
import { applicationStatusLabel } from "@/lib/applicationStatus";
import { candidateSourceLabel } from "@/lib/candidateSource";
import { sortStages } from "@/lib/coldSearch";
import { apiBase } from "@/lib/env";
import { safeAlert, safeHaptic } from "@/lib/telegramSafe";
import { useMarkColdCandidateViewed } from "@/lib/useMarkColdCandidateViewed";
import { WORKUA_OPEN_POLL_MS, workUaOpenInFlight } from "@/lib/workuaOpen";

export default function CandidateScreen(): JSX.Element {
  const { candidateId } = useParams();
  const [params] = useSearchParams();
  const initialVacancyId = params.get("vacancy");
  const [vacancyId, setVacancyId] = useState<string | null>(initialVacancyId);
  const qc = useQueryClient();
  const navigate = useNavigate();
  // Only a PUSH is sure to have an in-app entry behind it. The Telegram-card
  // deeplink lands here by REPLACE (App.tsx): a fresh location key with no
  // history, where navigate(-1) would do nothing.
  const cameInApp = useNavigationType() === "PUSH";
  const [noteDraft, setNoteDraft] = useState("");
  // Stage label chosen at push time, per vacancy: the row carries
  // ``pushed_to_pf_at`` but no stage.
  const [pushedStageByVacancy, setPushedStageByVacancy] = useState<
    Record<string, string>
  >({});

  const candidate = useQuery({
    queryKey: ["candidate", candidateId],
    queryFn: () => fetchCandidate(candidateId!),
    enabled: !!candidateId,
  });
  const markViewed = useMarkColdCandidateViewed(vacancyId, candidateId);
  useEffect(() => {
    if (candidate.isSuccess && vacancyId && candidateId) markViewed();
  }, [candidate.isSuccess, vacancyId, candidateId, markViewed]);
  const apps = useQuery({
    queryKey: ["candidate-apps", candidateId],
    queryFn: () => fetchCandidateApplications(candidateId!),
    enabled: !!candidateId,
  });
  const notes = useQuery({
    queryKey: ["candidate-notes", candidateId],
    queryFn: () => fetchCandidateNotes(candidateId!),
    enabled: !!candidateId,
  });

  useEffect(() => {
    if (!vacancyId && apps.data && apps.data.length > 0) {
      setVacancyId(apps.data[0]!.vacancy_id);
    }
  }, [apps.data, vacancyId]);

  const match = useQuery({
    queryKey: ["candidate-match", candidateId, vacancyId],
    queryFn: () => fetchCandidateMatch(candidateId!, vacancyId!),
    enabled: !!candidateId && !!vacancyId,
  });
  const cv = useQuery({
    queryKey: ["candidate-cv", candidateId],
    queryFn: () => fetchCandidateCv(candidateId!),
    enabled: !!candidateId,
  });

  // The vacancy the card was opened on need not hold an application of this
  // person — a cold-search hit has none. It still has to be a choice, or the
  // select shows another vacancy above this one's analysis and actions.
  const unappliedVacancyId =
    initialVacancyId &&
    apps.data &&
    !apps.data.some((a) => a.vacancy_id === initialVacancyId)
      ? initialVacancyId
      : null;

  const currentApplication = useMemo(() => {
    if (!apps.data || !vacancyId) return null;
    return apps.data.find((a) => a.vacancy_id === vacancyId) ?? null;
  }, [apps.data, vacancyId]);

  const noteMutation = useMutation({
    mutationFn: (body: string) =>
      postCandidateNote(
        candidateId!,
        WebApp.initDataUnsafe?.user?.id?.toString() ?? "",
        body,
      ),
    onSuccess: () => {
      setNoteDraft("");
      void qc.invalidateQueries({ queryKey: ["candidate-notes", candidateId] });
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: string) => deleteCandidateNote(candidateId!, noteId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["candidate-notes", candidateId] });
    },
    onError: () => safeHaptic("error"),
  });

  const coldResult = useQuery({
    queryKey: ["candidate-cold-result", candidateId, vacancyId],
    queryFn: () => fetchCandidateColdResult(vacancyId!, candidateId!),
    enabled: !!candidateId && !!vacancyId,
    // 404 (vacancy not readable) and 403 are terminal, not transient.
    retry: false,
    // A paid Work.ua open lands asynchronously; re-read the one row until it
    // settles (the server ends a stuck one within ten minutes).
    refetchInterval: (q) =>
      q.state.status !== "error" &&
      q.state.data &&
      workUaOpenInFlight(q.state.data)
        ? WORKUA_OPEN_POLL_MS
        : false,
  });
  const coldRow = coldResult.data ?? null;
  const coldSearchId = coldRow?.cold_search_id ?? null;
  // The open that just settled moved the contacts this card's header shows,
  // and the CV and score it queued come through the same queries.
  const coldInFlight = coldRow !== null && workUaOpenInFlight(coldRow);
  const wasInFlight = useRef(false);
  useEffect(() => {
    if (wasInFlight.current && !coldInFlight) {
      void qc.invalidateQueries({ queryKey: ["candidate", candidateId] });
      void qc.invalidateQueries({ queryKey: ["candidate-cv", candidateId] });
      void qc.invalidateQueries({ queryKey: ["candidate-match", candidateId] });
    }
    wasInFlight.current = coldInFlight;
  }, [coldInFlight, qc, candidateId]);

  // Same key and settings as the cold-search screen, so both share the cache.
  const stagesQuery = useQuery({
    queryKey: ["pipeline-stages", coldSearchId],
    queryFn: () => fetchPipelineStages(coldSearchId!),
    enabled: coldSearchId !== null,
    retry: false,
    staleTime: Infinity,
  });
  const tagsQuery = useQuery({
    queryKey: ["peopleforce-applicant-tags"],
    queryFn: fetchApplicantTags,
    enabled: coldSearchId !== null,
    retry: false,
    staleTime: Infinity,
  });

  // The shortlist and the vacancy's cold-search screen render the same row,
  // and a push creates the application this card's status and selector read.
  const invalidateColdRow = (): void => {
    void qc.invalidateQueries({
      queryKey: ["candidate-cold-result", candidateId, vacancyId],
    });
    void qc.invalidateQueries({ queryKey: ["candidate-apps", candidateId] });
    void qc.invalidateQueries({ queryKey: ["shortlist"] });
    void qc.invalidateQueries({
      queryKey: ["cold-search-accumulated", vacancyId],
    });
    void qc.invalidateQueries({ queryKey: ["cold-search"] });
  };

  const send = useMutation({
    mutationFn: (vars: {
      coldSearchId: string;
      resultId: string;
      applicantStateId?: number;
    }) =>
      sendColdSearchResultToPeopleForce(
        vars.coldSearchId,
        vars.resultId,
        vars.applicantStateId,
      ),
    onSuccess: () => {
      safeHaptic("success");
      invalidateColdRow();
    },
    onError: (err: Error) => {
      safeHaptic("error");
      safeAlert(`Не вдалося надіслати в PeopleForce: ${err.message}`);
    },
  });

  const dismiss = useMutation({
    mutationFn: (vars: { coldSearchId: string; resultId: string }) =>
      dismissColdSearchResult(vars.coldSearchId, vars.resultId),
    onSuccess: () => {
      safeHaptic("success");
      invalidateColdRow();
    },
    onError: (err: Error) => {
      safeHaptic("error");
      safeAlert(`Не вдалося приховати: ${err.message}`);
    },
  });

  const addToPool = useMutation({
    mutationFn: (vars: {
      coldSearchId: string;
      resultId: string;
      tagId: number;
    }) =>
      addColdSearchResultToTalentPool(
        vars.coldSearchId,
        vars.resultId,
        vars.tagId,
      ),
    onSuccess: () => {
      safeHaptic("success");
      invalidateColdRow();
    },
    onError: (err: Error) => {
      safeHaptic("error");
      safeAlert(`Не вдалося додати в кадровий резерв: ${err.message}`);
    },
  });

  if (!candidateId) {
    return <div className="p-4">Невірний id кандидата.</div>;
  }
  if (candidate.isLoading) {
    return <div className="p-4 text-tg-hint">Завантаження…</div>;
  }
  if (candidate.isError || !candidate.data) {
    return <div className="p-4 text-red-600">Не знайдено кандидата.</div>;
  }

  const c = candidate.data;
  const matchData = match.data;

  return (
    <div className="flex flex-col gap-4 p-4 pb-24">
      <button
        type="button"
        onClick={() => (cameInApp ? navigate(-1) : navigate("/shortlist"))}
        className="self-start text-xs text-tg-link"
      >
        ← Назад
      </button>

      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold">{c.full_name}</h1>
          <div className="mt-1 flex flex-col gap-1 text-sm text-tg-hint">
            {c.email && <span>{c.email}</span>}
            {c.phone_e164 && <span>{c.phone_e164}</span>}
            {c.city && <span>{c.city}</span>}
            <span>
              Джерело:{" "}
              {candidateSourceLabel(
                c.source,
                currentApplication?.peopleforce_source,
              )}
            </span>
          </div>
        </div>
        {matchData && (
          <ScoreBadge
            score={matchData.score}
            scaleMax={matchData.scale_max}
            category={matchData.category}
          />
        )}
      </header>

      {apps.data && apps.data.length + (unappliedVacancyId ? 1 : 0) > 1 && (
        <div>
          <label className="block text-xs uppercase text-tg-hint">
            Вакансія
          </label>
          <select
            value={vacancyId ?? ""}
            onChange={(e) => setVacancyId(e.target.value || null)}
            className="mt-1 w-full rounded-md border border-tg-secondary bg-tg-bg p-2 text-sm"
          >
            {unappliedVacancyId && (
              <option value={unappliedVacancyId}>
                Вакансія {unappliedVacancyId.slice(0, 8)} · без заявки
              </option>
            )}
            {apps.data.map((a) => (
              <option key={a.id} value={a.vacancy_id}>
                Вакансія {a.vacancy_id.slice(0, 8)} ·{" "}
                {applicationStatusLabel(a.status)}
              </option>
            ))}
          </select>
        </div>
      )}

      {matchData ? (
        <section className="rounded-lg bg-tg-secondary p-3">
          <h2 className="text-sm font-semibold">AI Аналіз</h2>
          <p className="mt-2 text-sm">{matchData.recommendation ?? "—"}</p>
          {matchData.criteria_scores &&
            matchData.criteria_scores.length > 0 && (
              <ul className="mt-3 flex flex-col gap-3">
                {matchData.criteria_scores.map((cs) => {
                  const pct = Math.max(
                    0,
                    Math.min(100, (cs.score / matchData.scale_max) * 100),
                  );
                  const weightPct =
                    typeof cs.weight === "number" && Number.isFinite(cs.weight)
                      ? Math.round(cs.weight * 100)
                      : null;
                  return (
                    <li key={cs.key}>
                      <div className="flex items-baseline justify-between gap-2 text-xs">
                        <span className="truncate font-medium text-tg-text">
                          {cs.label}
                        </span>
                        <span className="shrink-0 text-tg-hint">
                          {cs.score.toFixed(1)}/{matchData.scale_max}
                          {weightPct !== null ? ` · ${weightPct}%` : ""}
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-tg-bg">
                        <div
                          className="h-full rounded-full bg-tg-link"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      {(cs.evidence ?? cs.rationale) && (
                        <p className="mt-1 text-xs leading-snug text-tg-hint">
                          {cs.evidence ?? cs.rationale}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          <div className="mt-4 flex flex-col gap-2.5">
            <InsightSection
              label="Сильні сторони"
              items={matchData.strengths}
              tone="positive"
            />
            <InsightSection
              label="Слабкі сторони"
              items={matchData.weaknesses}
              tone="warning"
            />
            <InsightSection
              label="Бракує навичок"
              items={matchData.missing_skills}
              tone="warning"
            />
            <InsightSection
              label="Потребує уточнення"
              items={matchData.needs_clarification}
              tone="neutral"
            />
            <InsightSection
              label="Ризики"
              items={matchData.risks}
              tone="danger"
            />
          </div>
        </section>
      ) : match.isLoading ? (
        <div className="rounded-lg bg-tg-secondary p-3 text-sm text-tg-hint">
          AI оцінка ще не готова…
        </div>
      ) : (
        <div className="rounded-lg bg-tg-secondary p-3 text-sm text-tg-hint">
          Для цієї вакансії оцінки немає.
        </div>
      )}

      <section className="rounded-lg bg-tg-secondary p-3">
        <h2 className="text-sm font-semibold">CV</h2>
        {cv.isLoading && (
          <div className="mt-2 text-xs text-tg-hint">Завантаження…</div>
        )}
        {cv.data === null && (
          <div className="mt-2 text-xs text-tg-hint">CV ще не завантажено.</div>
        )}
        {cv.data && (
          <div className="mt-2 flex items-center justify-between gap-2">
            <div className="min-w-0 text-sm">
              <div className="truncate font-medium">{cv.data.filename}</div>
              <div className="text-xs text-tg-hint">
                {(cv.data.size_bytes / 1024).toFixed(0)} KB ·{" "}
                {cv.data.content_type}
              </div>
              <div className="text-xs text-tg-hint">
                parse: {cv.data.parse_status}
              </div>
            </div>
            <button
              type="button"
              className="rounded bg-tg-button px-3 py-1 text-sm text-tg-buttonText"
              onClick={() => {
                // download_url is now an API-relative path served by the
                // backend (the MinIO presigned host is not browser-reachable);
                // prepend the API origin so openLink gets an absolute URL.
                if (cv.data)
                  WebApp.openLink(`${apiBase()}${cv.data.download_url}`);
              }}
            >
              Відкрити
            </button>
          </div>
        )}
      </section>

      <section className="rounded-lg bg-tg-secondary p-3">
        <h2 className="text-sm font-semibold">Нотатки</h2>
        <ul className="mt-2 flex flex-col gap-2 text-sm">
          {notes.data?.map((n) => (
            <li key={n.id} className="rounded bg-tg-bg p-2">
              <div className="flex items-center justify-between gap-2 text-xs text-tg-hint">
                <span>{new Date(n.created_at).toLocaleString("uk-UA")}</span>
                <button
                  type="button"
                  onClick={() => deleteNoteMutation.mutate(n.id)}
                  disabled={deleteNoteMutation.isPending}
                  className="text-tg-hint underline disabled:opacity-40"
                >
                  Видалити
                </button>
              </div>
              <div>{n.body}</div>
            </li>
          ))}
          {notes.data && notes.data.length === 0 && (
            <li className="text-xs text-tg-hint">Поки нотаток немає.</li>
          )}
        </ul>
        <textarea
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          rows={3}
          placeholder="Додати нотатку…"
          className="mt-2 w-full rounded border border-tg-bg bg-tg-bg p-2 text-sm"
        />
        <button
          type="button"
          disabled={!noteDraft.trim() || noteMutation.isPending}
          onClick={() => noteMutation.mutate(noteDraft.trim())}
          className="mt-2 rounded bg-tg-button px-3 py-1 text-sm text-tg-buttonText disabled:opacity-50"
        >
          Додати
        </button>
      </section>

      {/* No status buttons on purpose (Roman, 24.09): the application PATCH
          lived only in our DB and the PeopleForce sync overwrote it. The
          actions are the cold-search screen's, which do reach PeopleForce. */}
      {(coldRow || currentApplication) && (
        <section className="rounded-lg bg-tg-secondary p-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Дії</h2>
            {currentApplication && (
              <span className="rounded-full bg-tg-bg px-2 py-0.5 text-xs text-tg-hint">
                статус: {applicationStatusLabel(currentApplication.status)}
              </span>
            )}
          </div>
          {coldRow ? (
            <div className="mt-2 flex flex-col gap-2 text-sm">
              <ColdResultActions
                key={vacancyId}
                result={coldRow}
                stages={sortStages(stagesQuery.data?.stages ?? [])}
                defaultStageId={stagesQuery.data?.default_stage_id ?? null}
                pushedStageName={
                  vacancyId ? pushedStageByVacancy[vacancyId] : undefined
                }
                onSend={(applicantStateId, stageName) => {
                  if (vacancyId && stageName !== undefined) {
                    setPushedStageByVacancy((m) => ({
                      ...m,
                      [vacancyId]: stageName,
                    }));
                  }
                  send.mutate({
                    coldSearchId: coldRow.cold_search_id,
                    resultId: coldRow.id,
                    applicantStateId,
                  });
                }}
                onDismiss={() =>
                  dismiss.mutate({
                    coldSearchId: coldRow.cold_search_id,
                    resultId: coldRow.id,
                  })
                }
                sending={send.isPending}
                dismissing={dismiss.isPending}
                tags={tagsQuery.data?.tags ?? []}
                tagsFailed={tagsQuery.isError}
                onAddToPool={(tagId, onFiled) =>
                  addToPool.mutate(
                    {
                      coldSearchId: coldRow.cold_search_id,
                      resultId: coldRow.id,
                      tagId,
                    },
                    { onSuccess: onFiled },
                  )
                }
                addingToPool={addToPool.isPending}
                // This card does not load the vacancy, so the claim is not
                // known here; a refused free re-score says why in its alert.
                mayOpenWorkUa
                onWorkUaOpenSettled={invalidateColdRow}
              />
            </div>
          ) : (
            // Only once the cold-search lookup has answered: while it loads the
            // actions may still be on their way.
            !coldResult.isLoading && (
              <p className="mt-1 text-xs text-tg-hint">
                Етап кандидата змінюється в PeopleForce — тут лише поточний
                статус заявки.
              </p>
            )
          )}
        </section>
      )}
    </div>
  );
}

interface InsightSectionProps {
  label: string;
  items: string[] | null | undefined;
  tone: "positive" | "warning" | "danger" | "neutral";
}

const INSIGHT_TONES: Record<
  InsightSectionProps["tone"],
  { badge: string; icon: string }
> = {
  positive: { badge: "bg-emerald-500", icon: "✓" },
  warning: { badge: "bg-amber-500", icon: "!" },
  danger: { badge: "bg-rose-500", icon: "⚠" },
  neutral: { badge: "bg-sky-500", icon: "?" },
};

function InsightSection({
  label,
  items,
  tone,
}: InsightSectionProps): JSX.Element | null {
  if (!items || items.length === 0) return null;
  const t = INSIGHT_TONES[tone];
  return (
    <div className="rounded-lg bg-tg-bg p-3">
      <div className="flex items-center gap-2">
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${t.badge}`}
        >
          {t.icon}
        </span>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-tg-text">
          {label}
        </h3>
        <span className="ml-auto text-xs text-tg-hint">{items.length}</span>
      </div>
      <ul className="mt-2 flex flex-col gap-1.5">
        {items.map((item, idx) => (
          <li
            key={idx}
            className="flex gap-2 text-sm leading-snug text-tg-text"
          >
            <span
              className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${t.badge}`}
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
